/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { AxiosError } from "axios";
import { Divider, Grid, Typography } from "@material-ui/core";
//custom libraries
import * as ROUTES from "./../constants/routes";
import { Button } from "../components";
import { BackButton, ConfirmationDialog } from "../components";
import * as ORDER from "../constants/order";

interface OrderDetailSideBarProps {
  orderDetail: any;
  handleLockUnLock: Function;
  cancelOrderByID: Function;
  checkout: Function;
  reOrderByOrderId: Function;
  lockOrder: Function;
  cusinfo: any;
}

interface OrderDialogOptions {
  message?: string;
  action?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  currentCartId?: string;
  takeOver?: boolean;
}

const OrderDetailSideBar: React.FC<OrderDetailSideBarProps> = ({
  orderDetail,
  handleLockUnLock,
  cancelOrderByID,
  checkout,
  reOrderByOrderId,
  lockOrder,
  cusinfo,
}) => {
  const location = useLocation<any>();
  const { t } = useTranslation();
  const [dialogOptions, setDialogOptions] = useState<OrderDialogOptions>({});
  const lockStatus = orderDetail.isLocked
    ? t("OrderManagement.StatusLocked")
    : orderDetail.checkLock
    ? t("OrderManagement.StatuscheckLock")
    : t("OrderManagement.StatusUnLocked");
  const lockAction = orderDetail.isLocked
    ? t("OrderManagement.TakeOverOrder")
    : orderDetail.checkLock
    ? t("OrderManagement.UnlockOrder")
    : t("OrderManagement.LockOrder");

  const onConfirm = () => {
    if (dialogOptions.action === ORDER.DIALOG_ACTION.CancelOrder) {
      cancelOrderByID();
      setDialogOptions({});
    }
    if (dialogOptions.action === ORDER.DIALOG_ACTION.ReOrder) {
      setDialogOptions({});
      reOrderByOrderId(true).catch((error: AxiosError) => {
        if (error.isAxiosError) {
          const errorResponse = error.response;
          if (
            errorResponse &&
            errorResponse.data &&
            errorResponse.data.errors
          ) {
            const e: any = errorResponse.data.errors[0];
            if (ORDER.LOCK_ORDRE_ERROR.includes(e.errorKey)) {
              setDialogOptions({
                message: t(`Error.${e.errorKey}`, {
                  orderNumber: e.errorParameters,
                }),
                confirmLabel: "ShopAs.LockOrderContinue.Yes",
                action: ORDER.DIALOG_ACTION.LockAndContinueReOder,
                currentCartId:
                  typeof e.errorParameters === "string"
                    ? e.errorParameters
                    : e.errorParameters[0],
                takeOver: e.errorKey === ORDER.TAKE_OVER_LOCK_ERROR,
              });
            } else {
              Promise.reject(error);
            }
          }
        } else {
          Promise.reject(error);
        }
      });
    }
    if (dialogOptions.action === ORDER.DIALOG_ACTION.LockAndContinueReOder) {
      setDialogOptions({});
      lockOrder(!!dialogOptions.takeOver, dialogOptions.currentCartId).then(
        () => reOrderByOrderId()
      );
    }
  };

  const onCancel = () => {
    setDialogOptions({});
  };

  return (
    <Grid container direction="column" spacing={2}>
      {orderDetail.orderStatus && (
        <>
          <Grid item>
            <Typography variant="h1">{orderDetail.orderId}</Typography>
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
          {orderDetail.orderStatus === ORDER.ORDER_STATUS.PendingOrder && (
            <>
              <Grid item>
                <Typography variant="h3" component="span">
                  {t("OrderManagement.LockStatus")}
                </Typography>
                <Typography variant="h4" component="span">
                  {lockStatus}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  fullWidth
                  name="manage order"
                  onClick={() => {
                    handleLockUnLock();
                  }}>
                  {lockAction}
                </Button>
              </Grid>
              <Grid item>
                <Divider />
              </Grid>
            </>
          )}

          <Grid item>
            <Typography variant="h3" component="span">
              {t("OrderManagement.OrderStatus")}
            </Typography>
            <Typography variant="h4" component="span">
              {t(`OrderManagement.Status.${orderDetail.orderStatus}`)}
            </Typography>
          </Grid>
          {orderDetail.orderStatus !== ORDER.ORDER_STATUS.PendingOrder ? (
            <>
              {orderDetail.orderStatus !== ORDER.ORDER_STATUS.Canceled && (
                <Grid item>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={() =>
                      setDialogOptions({
                        message: "OrderManagement.CancelOrderConfirmation",
                        confirmLabel: "OrderManagement.CancelOrder",
                        action: ORDER.DIALOG_ACTION.CancelOrder,
                      })
                    }
                    name="Cancel Order">
                    {t("OrderManagement.CancelOrder")}
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button
                  variant="outlined"
                  fullWidth
                  color="primary"
                  name="Re-Order"
                  disabled={!cusinfo || cusinfo.status === 0}
                  onClick={() =>
                    setDialogOptions({
                      message: t("OrderManagement.ReOrderConfirmation"),
                      confirmLabel: t("OrderManagement.ReOrder"),
                      action: ORDER.DIALOG_ACTION.ReOrder,
                    })
                  }>
                  {t("OrderManagement.ReOrder")}
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item>
              <Button
                variant="outlined"
                fullWidth
                color="primary"
                name="checkout"
                disabled={!orderDetail.checkLock}
                onClick={() => checkout()}>
                {t("OrderManagement.CheckoutAsUser")}
              </Button>
            </Grid>
          )}
          <ConfirmationDialog
            heading={dialogOptions.message}
            confirmLabel={dialogOptions.confirmLabel}
            cancelLabel={t("Table.Actions.Cancel")}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        </>
      )}
    </Grid>
  );
};

export default OrderDetailSideBar;
